import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import {
  ReviewDoubtLock,
  ReviewExtractControls,
} from '@/components/admin/sessions/review/review-extract-controls';
import { ReviewNoteEditor } from '@/components/admin/sessions/review/review-note-editor';
import { ReviewStamp } from '@/components/admin/sessions/review/review-stamp';
import { doubtState } from '@/lib/review-gestures';
import type { ReviewExtract } from '@matter_extractor/shared';
import { useState } from 'react';

/**
 * One extract in its case (relecture.md §5.5): its turn, the words, the « ? »
 * and the cross, then the note it carries and the line of its stamp.
 *
 * The note opens in place. An empty one clears it (`note-extract`), and a note
 * alone never makes a validation stale (D12) - that is the server's rule, not
 * this row's.
 */

export interface ReviewExtractRowProps {
  readonly extract: ReviewExtract;
}

export function ReviewExtractRow({ extract }: ReviewExtractRowProps) {
  const { apply, canWrite, busy, grid, review } = useReviewApi();
  const [editing, setEditing] = useState(false);
  const doubt =
    grid === null ? { locked: false, alsoIn: [] } : doubtState(extract, review.extracts, grid);

  const save = async (note: string): Promise<void> => {
    const applied = await apply([{ op: 'note-extract', extractId: extract.id, note: note.trim() }]);
    if (applied) setEditing(false);
  };

  return (
    <li
      data-testid={REVIEW_TEST_IDS.extract}
      data-extract={extract.id}
      data-turn={extract.turn}
      data-doubt={extract.doubt}
      data-locked={doubt.locked}
      className="grid grid-cols-[2.2rem_minmax(0,1fr)_auto] items-start gap-x-1.5 gap-y-1 text-caption"
    >
      <span className="text-gray-500 tabular-nums">t{extract.turn}</span>
      <span className="min-w-0 break-words">« {extract.text} »</span>
      <ReviewExtractControls extract={extract} doubt={doubt} />
      {doubt.locked ? (
        <span className="col-start-2 col-end-4">
          <ReviewDoubtLock doubt={doubt} />
        </span>
      ) : null}
      {editing ? (
        <div className="col-span-3">
          <ReviewNoteEditor
            label="Note de l’extrait"
            initial={extract.note}
            busy={busy}
            onSave={(text) => void save(text)}
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : extract.note === '' ? null : (
        <span
          data-testid={REVIEW_TEST_IDS.noteText}
          className="col-start-2 col-end-4 rounded-sm bg-gray-50 px-2 py-0.5 text-gray-700"
        >
          {extract.note}
        </span>
      )}
      <span className="col-start-2 col-end-4">
        <ReviewStamp stamp={extract}>
          {canWrite && !editing ? (
            <>
              <span aria-hidden="true">·</span>
              <button
                type="button"
                data-testid={REVIEW_TEST_IDS.noteOpen}
                className="cursor-pointer font-semibold text-bleu-nuit text-xs hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setEditing(true)}
              >
                {extract.note === '' ? 'noter' : 'modifier la note'}
              </button>
            </>
          ) : null}
        </ReviewStamp>
      </span>
    </li>
  );
}
